import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Chart, registerables } from 'chart.js';
import { merge, Subscription, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, skip, switchMap } from 'rxjs/operators';
import {
  PublisherDashboardApiService,
  RealtimeEventRowDto,
  RealtimeEventsDto,
  RealtimeRecordDto
} from '../../services/publisher-dashboard-api.service';
import { AuthService } from '../../services/auth.service';

Chart.register(...registerables);

const WINDOW_OPTIONS = [15, 30, 60];
// Delta polls: each one only reads the last few seconds (plus a server-side overlap).
const POLL_INTERVAL_MS = 20_000;
// Series drawn in the stacked chart; the remaining ones are folded into "other".
const MAX_SERIES = 6;
const SERIES_COLORS = ['#1e3a8a', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
const OTHER_COLOR = '#cbd5e1';
const OTHER_KEY = '__other__';

type Dimension = 'entity' | 'provider' | 'sourceType';

interface CountCell {
  m: number;        // minute, epoch ms
  e: string;        // entity id
  p: string;        // provider id ('' = none)
  s: string;        // source type ('' = none)
  c: number;
}

interface BreakdownRow {
  key: string;
  label: string;
  count: number;
  pct: number;
  sampled: number;
}

interface Filters {
  entity: string | null;
  provider: string | null;
  sourceType: string | null;
  minute: number | null;   // epoch ms
}

interface RevealedValues {
  CrmID: string | null;
  ExternalUID: string | null;
  Sha256Email: string | null;
  Sha256Phone: string | null;
}

const MASK_CHAR = '•';

@Component({
  selector: 'app-realtime',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './realtime.component.html',
  styleUrls: ['./realtime.component.scss']
})
export class RealtimeComponent implements OnInit, OnDestroy {
  @ViewChild('trendCanvas') trendCanvasRef?: ElementRef<HTMLCanvasElement>;

  readonly windowOptions = WINDOW_OPTIONS;
  windowMinutes = 15;
  live = true;

  loading = true;
  error: 'forbidden' | 'generic' | null = null;
  lastUpdate: number | null = null;
  polling = false;

  isGlobal = false;
  canUnmask = false;
  truncated = false;

  filters: Filters = { entity: null, provider: null, sourceType: null, minute: null };
  breakdownDim: Dimension = 'sourceType';

  // Derived, recomputed after every data change / filter change
  totalEvents = 0;
  lastMinuteEvents = 0;
  sampledShown = 0;
  sampledPct = 100;
  isSampled = false;
  breakdownRows: BreakdownRow[] = [];
  visibleRows: RealtimeEventRowDto[] = [];
  readonly tablePageSize = 100;
  tableLimit = this.tablePageSize;

  // Detail drawer
  detail: RealtimeRecordDto | null = null;
  detailRow: RealtimeEventRowDto | null = null;
  detailLoading = false;
  detailError: string | null = null;
  detailFields: { key: string; value: string }[] = [];
  detailIdentifiers: { key: string; value: string | null }[] = [];
  detailCustomVars: { Name: string; Type: string; Value: string }[] = [];

  private counts: CountCell[] = [];
  private rows: RealtimeEventRowDto[] = [];
  private seenEids = new Set<string>();
  private entityNames = new Map<string, string>();
  private providerNames = new Map<string, string>();
  private thresholds: Record<string, number> = {};
  private defaultThreshold = 1_000_000;
  private thresholdScale = 1_000_000;
  private lastTo: number | null = null;
  private generation = 0;
  private revealed = new Map<string, RevealedValues>();

  private chart: Chart | null = null;
  private readonly subscriptions = new Subscription();
  private loadSub: Subscription | null = null;
  private pollSub: Subscription | null = null;
  private detailSub: Subscription | null = null;
  private readonly locale = window?.navigator?.language || 'en-US';
  private readonly timeFmt = new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit' });
  private readonly timeSecFmt = new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  constructor(
    private api: PublisherDashboardApiService,
    private translate: TranslateService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Same reload trigger as the Analytics dashboard: entity change or impersonation change.
    this.subscriptions.add(
      merge(
        this.authService.selectedDcr$.pipe(distinctUntilChanged(), skip(1)),
        this.authService.currentUser$.pipe(map(u => u?.ID ?? null), distinctUntilChanged(), skip(1))
      )
        .pipe(debounceTime(0), switchMap(() => this.authService.waitForEntitySync()))
        .subscribe(() => {
          this.filters = { entity: null, provider: null, sourceType: null, minute: null };
          this.reload();
        })
    );
    this.subscriptions.add(this.authService.waitForEntitySync().subscribe(() => this.reload()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.loadSub?.unsubscribe();
    this.stopPolling();
    this.detailSub?.unsubscribe();
    this.chart?.destroy();
    this.chart = null;
  }

  // Polls are paused while the tab is hidden: nobody is looking and each poll is a query.
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.hidden) {
      this.stopPolling();
    } else if (this.live && !this.loading && !this.error) {
      this.resume();
    }
  }

  // Back from a pause (hidden tab or "Pause"). A gap longer than the window would make the
  // catch-up poll read the whole window anyway: take a fresh snapshot instead, which costs the
  // same but recalibrates the sampling thresholds and goes through the shared snapshot cache.
  private resume(): void {
    if (this.lastTo !== null && Date.now() - this.lastTo > this.windowMinutes * 60_000) {
      this.reload();
    } else {
      this.startPolling(true);
    }
  }

  // ----- Header actions -----

  selectWindow(minutes: number): void {
    if (minutes === this.windowMinutes) return;
    this.windowMinutes = minutes;
    this.filters.minute = null;
    this.reload();
  }

  toggleLive(): void {
    this.live = !this.live;
    if (this.live) this.resume();
    else this.stopPolling();
  }

  setBreakdown(dim: Dimension): void {
    this.breakdownDim = dim;
    this.recompute();
  }

  // ----- Drill-down -----

  drill(row: BreakdownRow): void {
    if (this.breakdownDim === 'entity') {
      this.filters.entity = row.key;
      this.breakdownDim = 'provider';
    } else if (this.breakdownDim === 'provider') {
      this.filters.provider = row.key;
      this.breakdownDim = 'sourceType';
    } else {
      this.filters.sourceType = this.filters.sourceType === row.key ? null : row.key;
    }
    this.tableLimit = this.tablePageSize;
    this.recompute();
  }

  clearFilter(key: keyof Filters): void {
    this.filters[key] = null;
    if (key === 'entity' && this.isGlobal && !this.filters.provider) this.breakdownDim = 'entity';
    this.tableLimit = this.tablePageSize;
    this.recompute();
  }

  clearAllFilters(): void {
    this.filters = { entity: null, provider: null, sourceType: null, minute: null };
    this.breakdownDim = this.isGlobal ? 'entity' : 'sourceType';
    this.tableLimit = this.tablePageSize;
    this.recompute();
  }

  get hasFilters(): boolean {
    return !!(this.filters.entity || this.filters.provider || this.filters.sourceType || this.filters.minute !== null);
  }

  get availableDims(): Dimension[] {
    const dims: Dimension[] = [];
    if (this.isGlobal && !this.filters.entity) dims.push('entity');
    if (!this.filters.provider) dims.push('provider');
    dims.push('sourceType');
    return dims;
  }

  showMoreRows(): void {
    this.tableLimit += this.tablePageSize;
  }

  get pagedRows(): RealtimeEventRowDto[] {
    return this.visibleRows.slice(0, this.tableLimit);
  }

  // ----- Labels -----

  entityLabel(id: string | null): string {
    if (!id) return '—';
    return this.entityNames.get(id) || id;
  }

  providerLabel(id: string | null): string {
    if (!id) return this.translate.instant('realtime.labels.none');
    return this.providerNames.get(id) || id;
  }

  sourceTypeLabel(s: string | null): string {
    return s || this.translate.instant('realtime.labels.none');
  }

  minuteLabel(ms: number): string {
    return this.timeFmt.format(new Date(ms));
  }

  timeLabel(ms: number): string {
    return this.timeSecFmt.format(new Date(ms));
  }

  formatNumber(n: number): string {
    return new Intl.NumberFormat(this.locale).format(n);
  }

  formatPct(p: number): string {
    const digits = p >= 10 ? 0 : p >= 1 ? 1 : p >= 0.1 ? 2 : 3;
    return new Intl.NumberFormat(this.locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(p);
  }

  isMasked(v: string | null): boolean {
    return !!v && v.startsWith(MASK_CHAR);
  }

  cell(row: RealtimeEventRowDto, field: keyof RevealedValues): string | null {
    const r = this.revealed.get(row.EID);
    return r ? r[field] : row[field];
  }

  isRevealed(row: RealtimeEventRowDto): boolean {
    return this.revealed.has(row.EID);
  }

  hasMaskedValues(row: RealtimeEventRowDto): boolean {
    return [row.CrmID, row.ExternalUID, row.Sha256Email, row.Sha256Phone].some(v => this.isMasked(v));
  }

  // ----- Detail drawer -----

  openDetail(row: RealtimeEventRowDto): void {
    this.detailRow = row;
    this.loadDetail(row, this.revealed.has(row.EID));
  }

  closeDetail(): void {
    this.detailSub?.unsubscribe();
    this.detail = null;
    this.detailRow = null;
    this.detailError = null;
    this.detailLoading = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.detailRow) this.closeDetail();
  }

  unmaskDetail(): void {
    if (this.detailRow) this.loadDetail(this.detailRow, true);
  }

  revealRow(row: RealtimeEventRowDto, event: Event): void {
    event.stopPropagation();
    this.api.getRealtimeRecord({ EntityID: row.EntityID, EID: row.EID, CreationTime: row.CreationTime, Unmask: true })
      .subscribe({
        next: res => {
          if (res.payload) this.revealed.set(row.EID, this.pickRevealed(res.payload.Record));
        },
        error: () => { /* the eye simply stays closed */ }
      });
  }

  private loadDetail(row: RealtimeEventRowDto, unmask: boolean): void {
    this.detailSub?.unsubscribe();
    this.detailLoading = true;
    this.detailError = null;
    this.detailSub = this.api.getRealtimeRecord({
      EntityID: row.EntityID, EID: row.EID, CreationTime: row.CreationTime, Unmask: unmask
    }).subscribe({
      next: res => {
        this.detailLoading = false;
        if (!res.payload) {
          this.detailError = this.translate.instant('realtime.detail.notFound');
          return;
        }
        this.detail = res.payload;
        if (!res.payload.Masked) this.revealed.set(row.EID, this.pickRevealed(res.payload.Record));
        this.buildDetailFields(res.payload.Record);
      },
      error: err => {
        this.detailLoading = false;
        this.detailError = this.translate.instant(err?.status === 403 ? 'realtime.status.forbidden' : 'realtime.detail.error');
      }
    });
  }

  private pickRevealed(rec: Record<string, unknown>): RevealedValues {
    const s = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));
    return {
      CrmID: s(rec['CrmID']),
      ExternalUID: s(rec['ExternalUID']),
      Sha256Email: s(rec['Sha256LcE']) ?? s(rec['Sha256UcE']),
      Sha256Phone: s(rec['Sha256NcP']) ?? s(rec['Sha256WcP']),
    };
  }

  private buildDetailFields(rec: Record<string, unknown>): void {
    const ids = this.pickRevealed(rec);
    const tncid = rec['TNCID'];
    this.detailIdentifiers = [
      { key: 'TNCID', value: tncid === null || tncid === undefined || tncid === '' ? null : String(tncid) },
      { key: 'CrmID', value: ids.CrmID },
      { key: 'ExternalUID', value: ids.ExternalUID },
      { key: this.translate.instant('realtime.table.email'), value: ids.Sha256Email },
      { key: this.translate.instant('realtime.table.phone'), value: ids.Sha256Phone },
    ];

    const fields: { key: string; value: string }[] = [];
    for (const [key, value] of Object.entries(rec)) {
      if (key === 'CustomVariables') continue;
      if (value === null || value === undefined || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      fields.push({ key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) });
    }
    this.detailFields = fields;
    const cv = rec['CustomVariables'];
    this.detailCustomVars = Array.isArray(cv)
      ? cv.map((x: any) => ({ Name: String(x?.Name ?? ''), Type: String(x?.Type ?? ''), Value: x?.Value == null ? '' : String(x.Value) }))
      : [];
  }

  // ----- Loading -----

  retry(): void {
    this.reload();
  }

  private reload(): void {
    this.generation++;
    const gen = this.generation;
    this.stopPolling();
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.error = null;

    this.loadSub = this.api.getRealtimeEvents({ WindowMinutes: this.windowMinutes }).subscribe({
      next: res => {
        if (gen !== this.generation) return;
        this.loading = false;
        if (!res.payload) {
          this.error = 'generic';
          return;
        }
        this.resetData(res.payload);
        this.applyResponse(res.payload);
        if (this.live && !document.hidden) this.startPolling(false);
      },
      error: err => {
        if (gen !== this.generation) return;
        this.loading = false;
        this.error = err?.status === 403 ? 'forbidden' : 'generic';
      }
    });
  }

  private startPolling(immediate: boolean): void {
    this.stopPolling();
    if (this.lastTo === null) return;
    this.pollSub = timer(immediate ? 0 : POLL_INTERVAL_MS, POLL_INTERVAL_MS).subscribe(() => this.poll());
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private poll(): void {
    if (this.polling || this.lastTo === null) return;
    const gen = this.generation;
    this.polling = true;
    this.api.getRealtimeEvents({
      WindowMinutes: this.windowMinutes,
      Since: this.lastTo,
      Thresholds: this.thresholds,
      DefaultThreshold: this.defaultThreshold,
    }).subscribe({
      next: res => {
        this.polling = false;
        if (gen !== this.generation || !res.payload) return;
        this.applyResponse(res.payload);
      },
      error: err => {
        this.polling = false;
        // A transient failure just skips this beat; losing the permission stops the page.
        if (err?.status === 403) {
          this.stopPolling();
          this.error = 'forbidden';
        }
      }
    });
  }

  private resetData(res: RealtimeEventsDto): void {
    this.counts = [];
    this.rows = [];
    this.seenEids.clear();
    this.revealed.clear();
    this.thresholds = { ...res.Thresholds };
    this.defaultThreshold = res.DefaultThreshold;
    this.thresholdScale = res.ThresholdScale || 1_000_000;
    this.isGlobal = res.Global;
    if (!this.isGlobal && this.breakdownDim === 'entity') this.breakdownDim = 'sourceType';
    if (this.isGlobal && !this.filters.entity && !this.filters.provider) this.breakdownDim = 'entity';
    this.tableLimit = this.tablePageSize;
  }

  /** Merges a snapshot or a delta into the FIFO state, then evicts what fell out of the window. */
  private applyResponse(res: RealtimeEventsDto): void {
    this.canUnmask = res.CanUnmask;
    this.truncated = res.Truncated;
    res.Entities.forEach(e => this.entityNames.set(e.ID, e.Name || e.ID));
    res.Providers.forEach(p => { if (p.ID) this.providerNames.set(p.ID, p.Name || p.ID); });
    for (const [e, th] of Object.entries(res.Thresholds || {})) {
      if (this.thresholds[e] === undefined) this.thresholds[e] = th;
    }

    // Counts: the response covers whole minutes >= From, so those buckets are replaced.
    const fresh: CountCell[] = res.Counts.map(([m, ei, pi, si, c]) => ({
      m: m * 1000,
      e: res.Entities[ei]?.ID ?? '',
      p: res.Providers[pi]?.ID ?? '',
      s: res.SourceTypes[si] ?? '',
      c,
    }));
    this.counts = this.counts.filter(c => c.m < res.From).concat(fresh);

    // Sample rows: deterministic sampling + overlap → de-duplicate by EID.
    const added = res.Rows.filter(r => !this.seenEids.has(r.EID));
    added.forEach(r => this.seenEids.add(r.EID));
    if (added.length > 0) {
      this.rows = added.concat(this.rows).sort((a, b) => b.CreationTime - a.CreationTime);
    }

    this.lastTo = res.To;
    this.lastUpdate = res.To;
    this.evict(res.To);
    this.recompute();
  }

  private evict(now: number): void {
    const windowStartMinute = Math.floor((now - this.windowMinutes * 60000) / 60000) * 60000;
    this.counts = this.counts.filter(c => c.m >= windowStartMinute);
    const cutoff = now - this.windowMinutes * 60000;
    if (this.rows.length && this.rows[this.rows.length - 1].CreationTime < cutoff) {
      const kept: RealtimeEventRowDto[] = [];
      for (const r of this.rows) {
        if (r.CreationTime >= cutoff) kept.push(r);
        else { this.seenEids.delete(r.EID); this.revealed.delete(r.EID); }
      }
      this.rows = kept;
    }
    if (this.filters.minute !== null && this.filters.minute < windowStartMinute) this.filters.minute = null;
  }

  // ----- Derived state -----

  private matches(e: string, p: string, s: string, m: number, ignoreMinute = false): boolean {
    const f = this.filters;
    if (f.entity && e !== f.entity) return false;
    if (f.provider !== null && p !== f.provider) return false;
    if (f.sourceType !== null && s !== f.sourceType) return false;
    if (!ignoreMinute && f.minute !== null && m !== f.minute) return false;
    return true;
  }

  private dimKey(dim: Dimension, e: string, p: string, s: string): string {
    return dim === 'entity' ? e : dim === 'provider' ? p : s;
  }

  dimLabel(dim: Dimension, key: string): string {
    if (key === OTHER_KEY) return this.translate.instant('realtime.labels.other');
    return dim === 'entity' ? this.entityLabel(key) : dim === 'provider' ? this.providerLabel(key || null) : this.sourceTypeLabel(key || null);
  }

  private recompute(): void {
    const dim = this.breakdownDim;
    const now = this.lastTo ?? Date.now();
    const lastFullMinute = Math.floor(now / 60000) * 60000 - 60000;

    // Exact counts (filtered)
    let total = 0;
    let lastMinute = 0;
    const byKey = new Map<string, number>();
    for (const c of this.counts) {
      if (!this.matches(c.e, c.p, c.s, c.m)) continue;
      total += c.c;
      if (c.m === lastFullMinute) lastMinute += c.c;
      const k = this.dimKey(dim, c.e, c.p, c.s);
      byKey.set(k, (byKey.get(k) || 0) + c.c);
    }

    // Sample (filtered)
    const visible: RealtimeEventRowDto[] = [];
    const sampledByKey = new Map<string, number>();
    for (const r of this.rows) {
      const m = Math.floor(r.CreationTime / 60000) * 60000;
      if (!this.matches(r.EntityID, r.ProviderID ?? '', r.SourceType ?? '', m)) continue;
      visible.push(r);
      const k = this.dimKey(dim, r.EntityID, r.ProviderID ?? '', r.SourceType ?? '');
      sampledByKey.set(k, (sampledByKey.get(k) || 0) + 1);
    }

    this.totalEvents = total;
    this.lastMinuteEvents = lastMinute;
    this.visibleRows = visible;
    this.sampledShown = visible.length;
    // Effective rate = sampled rows / exact events, for the current filters.
    this.sampledPct = total > 0 ? Math.min(100, (visible.length / total) * 100) : 100;
    const relevantEntities = this.filters.entity ? [this.filters.entity] : Object.keys(this.thresholds);
    this.isSampled = relevantEntities.some(e => (this.thresholds[e] ?? this.defaultThreshold) < this.thresholdScale);

    this.breakdownRows = [...byKey.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({
        key,
        label: this.dimLabel(dim, key),
        count,
        pct: total > 0 ? (count / total) * 100 : 0,
        sampled: sampledByKey.get(key) || 0,
      }));

    this.renderChart();
  }

  // ----- Chart -----

  private renderChart(): void {
    // The canvas is inside an *ngIf that flips with `loading`: wait for it.
    setTimeout(() => this.drawChart());
  }

  private drawChart(): void {
    const canvas = this.trendCanvasRef?.nativeElement;
    if (!canvas) return;

    const dim = this.breakdownDim;
    const now = this.lastTo ?? Date.now();
    const start = Math.floor((now - this.windowMinutes * 60000) / 60000) * 60000;
    const minutes: number[] = [];
    for (let m = start; m <= now; m += 60000) minutes.push(m);
    const minuteIdx = new Map(minutes.map((m, i) => [m, i]));

    // Top series over the window (chart ignores the minute filter: it is how you pick it)
    const totals = new Map<string, number>();
    for (const c of this.counts) {
      if (!this.matches(c.e, c.p, c.s, c.m, true)) continue;
      const k = this.dimKey(dim, c.e, c.p, c.s);
      totals.set(k, (totals.get(k) || 0) + c.c);
    }
    const top = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, MAX_SERIES).map(x => x[0]);
    const topSet = new Set(top);
    const seriesKeys = totals.size > top.length ? [...top, OTHER_KEY] : top;
    const data = new Map(seriesKeys.map(k => [k, new Array(minutes.length).fill(0)]));
    for (const c of this.counts) {
      if (!this.matches(c.e, c.p, c.s, c.m, true)) continue;
      const i = minuteIdx.get(c.m);
      if (i === undefined) continue;
      const k = this.dimKey(dim, c.e, c.p, c.s);
      data.get(topSet.has(k) ? k : OTHER_KEY)![i] += c.c;
    }

    const selected = this.filters.minute;
    const datasets = seriesKeys.map((k, si) => {
      const color = k === OTHER_KEY ? OTHER_COLOR : SERIES_COLORS[si % SERIES_COLORS.length];
      return {
        label: this.dimLabel(dim, k),
        data: data.get(k)!,
        backgroundColor: minutes.map(m => (selected === null || m === selected ? color : color + '55')),
        borderWidth: 0,
        stack: 'events',
      };
    });
    const labels = minutes.map(m => this.minuteLabel(m));

    // The canvas is re-created when the template toggles (loading/error): rebind the chart.
    if (this.chart && this.chart.canvas !== canvas) {
      this.chart.destroy();
      this.chart = null;
    }

    if (this.chart) {
      this.chart.data.labels = labels;
      this.chart.data.datasets = datasets as any;
      (this.chart as any).$minutes = minutes;
      this.chart.update('none');
      return;
    }

    this.chart = new Chart(canvas, {
      type: 'bar',
      data: { labels, datasets: datasets as any },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: { stacked: true, grid: { display: false }, ticks: { maxRotation: 0, autoSkipPadding: 12 } },
          y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
        },
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10 } },
          tooltip: {
            callbacks: {
              footer: items => {
                const sum = items.reduce((a, i) => a + (Number(i.raw) || 0), 0);
                return `${this.translate.instant('realtime.chart.total')}: ${this.formatNumber(sum)}`;
              }
            }
          }
        },
        onClick: (_evt, elements, chart) => {
          const mins: number[] = (chart as any).$minutes || [];
          const idx = elements[0]?.index;
          if (idx === undefined) return;
          const m = mins[idx];
          this.filters.minute = this.filters.minute === m ? null : m;
          this.tableLimit = this.tablePageSize;
          this.recompute();
        },
        onHover: (evt, elements) => {
          const target = evt.native?.target as HTMLElement | undefined;
          if (target) target.style.cursor = elements.length ? 'pointer' : 'default';
        }
      }
    });
    (this.chart as any).$minutes = minutes;
  }
}
